import {
	Button,
	Carousel,
	type CarouselApi,
	CarouselContent,
	CarouselItem,
} from "@components";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Image } from "@/components/ui/image";
import { IMAGES } from "../assets";

type OnboardingStep = {
	id: number;
	title: string;
	description: ReactNode;
	illustration: ReactNode;
};

function DeleteMarkersIllustrations() {
	return (
		<div className="mx-auto flex w-full max-w-[456px] flex-col gap-8">
			<div className="flex flex-col gap-2">
				<Image
					alt="Ilustração: toque no marcador PIN para selecioná-lo"
					className="mx-auto max-w-full"
					height={272}
					priority
					src={IMAGES.IllustrationStep4}
					width={303}
				/>
				<p className="font-body font-normal text-sm text-tertiary">
					Clique no <b className="font-bold text-primary">PIN</b> que deseja
					excluir.
				</p>
			</div>
			<div className="flex flex-col gap-2">
				<Image
					alt="Ilustração: botão Excluir marcador ao lado do PIN selecionado"
					className="mx-auto max-w-full"
					height={272}
					priority
					src={IMAGES.IllustrationStep5}
					width={448}
				/>
				<p className="font-body font-normal text-sm text-tertiary">
					Depois clique no Botão{" "}
					<b className="font-bold text-primary">Excluir Marcador</b>.
				</p>
			</div>
		</div>
	);
}

const ONBOARDING: OnboardingStep[] = [
	{
		id: 1,
		illustration: (
			<Image
				alt="Image onboarding"
				className="mx-auto"
				height={232}
				priority
				src={IMAGES.IllustrationStep1}
				width={219}
			/>
		),
		title: "Como tirar boas fotos da vistoria",
		description: (
			<>
				Siga <b className="text-primary">essas 2 dicas simples</b> antes de
				tirar suas fotos para ter o melhor resultado possível.
			</>
		),
	},
	{
		id: 2,
		illustration: (
			<Image
				alt="Image onboarding"
				className="mx-auto"
				height={324}
				priority
				src={IMAGES.IllustrationStep2}
				width={162}
			/>
		),
		title: "Use o celular na horizontal",
		description: (
			<>
				Busque <b className="text-primary">deixar algumas bordas na foto</b>,
				assim você mostra melhor o veículo inteiro.
			</>
		),
	},
	{
		id: 3,
		illustration: (
			<Image
				alt="Image onboarding"
				className="mx-auto"
				height={271}
				priority
				src={IMAGES.IllustrationStep3}
				width={345}
			/>
		),
		title: "Como utilizar os marcadores",
		description: (
			<>
				Não utilize múltiplos marcadores em uma mesma avaria,{" "}
				<b className="text-primary">
					para marcar uma área, selecione um pin da lista, toque na tela e
					arraste.
				</b>
			</>
		),
	},
	{
		id: 4,
		illustration: <DeleteMarkersIllustrations />,
		title: "Como excluir os marcadores",
		description: (
			<>
				A partir de agora, cada <b className="font-bold text-primary">PIN</b>{" "}
				pode ser excluído individualmente.
			</>
		),
	},
];

type PhotoOnboardingProps = {
	onClick?: () => void;
	buttonText?: string;
};

export const PhotoOnboarding = ({
	onClick,
	buttonText,
}: PhotoOnboardingProps) => {
	const [api, setApi] = useState<CarouselApi>();
	const [current, setCurrent] = useState(0);

	useEffect(() => {
		if (!api) {
			return;
		}

		setCurrent(api.selectedScrollSnap());

		api.on("select", () => {
			setCurrent(api.selectedScrollSnap());
		});
	}, [api]);

	const totalCount = api?.scrollSnapList().length;
	const currentIndex = api?.selectedScrollSnap();

	const isLast = currentIndex === (totalCount || 0) - 1;

	const handleNextStep = () => {
		if (!isLast) {
			api?.scrollNext();
			return;
		}
		onClick?.();
	};

	const getButtonText = (): string => {
		if (isLast && buttonText) {
			return "Entendi";
		}
		if (buttonText) {
			return buttonText;
		}
		if (isLast) {
			return "Tirar foto";
		}
		return "Continuar";
	};

	return (
		<div className="fixed top-0 left-0 z-50 flex h-dvh w-full min-w-dvw flex-col bg-secondary">
			<div className="relative flex h-full w-full flex-col">
				<div className="scrollbar-hide relative flex w-full flex-1 flex-col items-center gap-5 overflow-y-scroll px-4 py-5">
					<div className="mx-auto flex w-full max-w-[456px] flex-col gap-2">
						<h5 className="font-body font-bold text-secondary text-xl">
							{ONBOARDING[current || 0].title}
						</h5>
						<p className="font-body font-normal text-sm text-tertiary">
							{ONBOARDING[current || 0].description}
						</p>
					</div>

					<Carousel
						className="m-auto w-full max-w-[456px]"
						opts={{
							loop: false,
							startIndex: 0,
							watchDrag: false,
						}}
						setApi={setApi}
					>
						<CarouselContent>
							{ONBOARDING.map((item) => (
								<CarouselItem className="my-auto" key={item.id}>
									{item.illustration}
								</CarouselItem>
							))}
						</CarouselContent>
					</Carousel>
				</div>

				<div className="w-full border-primary border-t bg-secondary px-4 pt-4 pb-8 text-center">
					<Button
						className="w-full max-w-[456px]"
						onClick={handleNextStep}
						type="button"
					>
						{getButtonText()}
					</Button>
				</div>
			</div>
		</div>
	);
};
